import { NumberField } from 'components/form-fields-2';
import type { ExpenseLineItem } from '../types';
import styles from './buyer-commission-editor.module.scss';

type CommissionType = 'fixed' | 'percent';

interface BuyerCommissionEditorProps {
  item: ExpenseLineItem;
  onUpdate: (patch: Partial<ExpenseLineItem>) => void;
}

interface CommissionRowProps {
  id: string;
  label: string;
  type: CommissionType;
  value: number | null;
  onTypeChange: (type: CommissionType) => void;
  onValueChange: (value: number | null) => void;
}

/**
 * A single At Target / At Max row: a Fixed($)/Percent(%) toggle plus a value
 * input. The toggle and value are independent of the sibling row.
 * @param {CommissionRowProps} props - The row label, type, value and handlers
 * @return {JSX.Element} The commission row
 */
function CommissionRow({
  id,
  label,
  type,
  value,
  onTypeChange,
  onValueChange,
}: CommissionRowProps): JSX.Element {
  const isPercent = type === 'percent';

  return (
    <div className={styles.row}>
      <span className={styles.label}>{label}</span>
      <div className={styles.seg} role="group" aria-label={`${label} unit`}>
        <button
          type="button"
          className={`${styles.segBtn} ${
            !isPercent ? styles.segBtnActive : ''
          }`}
          aria-pressed={!isPercent}
          onClick={() => onTypeChange('fixed')}
        >
          $
        </button>
        <button
          type="button"
          className={`${styles.segBtn} ${isPercent ? styles.segBtnActive : ''}`}
          aria-pressed={isPercent}
          onClick={() => onTypeChange('percent')}
        >
          %
        </button>
      </div>
      <NumberField
        id={id}
        hideLabel
        light
        size="sm"
        className={styles.value}
        placeholder={isPercent ? '0.00%' : '$0.00'}
        value={value}
        onChange={onValueChange}
      />
    </div>
  );
}

/**
 * Buyer Commission amount editor: replaces the standard Unit + Value cells for
 * line items whose subtype is "Buyer Commission". Exposes two independently
 * togglable Fixed($)/Percent(%) values — one at the target price, one at the
 * max price.
 * @param {BuyerCommissionEditorProps} props - The line item and update handler
 * @return {JSX.Element} The buyer commission editor
 */
export function BuyerCommissionEditor({
  item,
  onUpdate,
}: BuyerCommissionEditorProps): JSX.Element {
  const targetType = item.targetAmountType ?? 'percent';
  const maxType = item.maxAmountType ?? 'percent';

  return (
    <div className={styles.bc}>
      <CommissionRow
        id={`li-bc-target-${item.id}`}
        label="At Target"
        type={targetType}
        value={item.targetAmount ?? null}
        onTypeChange={(t) => onUpdate({ targetAmountType: t })}
        onValueChange={(v) => onUpdate({ targetAmount: v ?? undefined })}
      />
      <CommissionRow
        id={`li-bc-max-${item.id}`}
        label="At Max"
        type={maxType}
        value={item.maxAmount ?? null}
        onTypeChange={(t) => onUpdate({ maxAmountType: t })}
        onValueChange={(v) => onUpdate({ maxAmount: v ?? undefined })}
      />
    </div>
  );
}
